import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import useMembers from '@/hooks/useMembers';
import { Member } from '@/shared/types/member';

import { FilterCheckboxItem } from './FilterCheckboxItem';
import { memberSearchTerms } from './filterSearchTerms';
import { SearchableCheckboxList } from './SearchableCheckboxList';
import { FilterPanelProps } from './types';

const toAgentIds = (agentIds: unknown): string[] =>
  Array.isArray(agentIds)
    ? agentIds.map((item: { value: string } | string) =>
        typeof item === 'string' ? item : item.value
      )
    : [];

/**
 * Renders selectable agents (human team members) for account-level filtering.
 * Used in analytics pages like Human Agents Effectiveness and Agents Performance.
 */
export const AgentsFilter = ({
  filterIndex,
  searchText = '',
  onSearchChange = () => {},
}: FilterPanelProps) => {
  const { t } = useTranslation();
  const { members } = useMembers();
  const { control, getValues } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const agentIds = useWatch({ control, name: `filters.${filterIndex}.agentIds` }) || [];

  const toggleAgent = (agentId: string, checked: boolean) => {
    const currentAgentIds = toAgentIds(agentIds);

    const nextAgentIds = checked
      ? [...currentAgentIds, agentId]
      : currentAgentIds.filter((id: string) => id !== agentId);

    const currentFilter = getValues(`filters.${filterIndex}`);
    const nameById = new Map(members?.map((member) => [member.user_id, member.name]));
    const formattedAgentIds = nextAgentIds.map((id: string) => ({
      value: id,
      label: nameById.get(id) ?? id,
    }));

    update(filterIndex, { ...currentFilter, agentIds: formattedAgentIds });
  };

  if (!members || members.length === 0) {
    return <FilterEmptyState text={t('analytics.filters.no_agents')} />;
  }

  const currentIds = toAgentIds(agentIds);

  return (
    <SearchableCheckboxList
      searchText={searchText}
      onSearchChange={onSearchChange}
      items={members}
      getSearchTerms={(member: Member) => memberSearchTerms(member)}
      placeholder={t('members.search_placeholder')}
      renderItem={(member: Member) => (
        <FilterCheckboxItem
          key={member.user_id}
          id={`agent-${member.user_id}`}
          checked={currentIds.includes(member.user_id)}
          onCheckedChange={(checked) => toggleAgent(member.user_id, checked as boolean)}
          className="h-auto"
        >
          <div className="flex min-w-0 flex-col">
            <span className="truncate">{member.name}</span>
            <span className="truncate text-xs text-foreground-muted">{member.email}</span>
          </div>
        </FilterCheckboxItem>
      )}
    />
  );
};
